import { useCallback, useMemo, useRef, type MutableRefObject, type RefObject } from 'react';

import {
    AXIS_PREVIEW_SALES_KPI_IDS,
    buildPackArchitectureTableTitle,
    getAxisLabel,
} from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import {
    collectConfiguredPackArchitectureKpiIds,
    createPackArchitectureKpiIdMatcher,
} from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';

import { buildPackArchitectureTableColumns } from './packArchitectureTable.columns';
import {
    buildTableData,
    formatTableKpiCheckboxLabel,
    getPackArchitectureTableEmptyDescription,
    getPackArchitectureTableFooterLabel,
    MAX_VISIBLE_COLUMNS,
    TABLE_EMPTY_STATE_MIN_HEIGHT,
    TABLE_SCROLL_Y,
} from './packArchitectureTable.helpers';
import type { KpiOption, TableRow } from './packArchitectureTable.helpers';
import { usePackArchitectureTableScroll } from './usePackArchitectureTableScroll';

type UsePackArchitectureTableParams = {
    tableWrapperRef?: RefObject<HTMLDivElement | null>;
    isSyncingTableFromChartRef?: MutableRefObject<boolean>;
    suppressChartToTableSyncRef?: MutableRefObject<boolean>;
};

export function usePackArchitectureTable({
    tableWrapperRef: tableWrapperRefProp,
    isSyncingTableFromChartRef,
    suppressChartToTableSyncRef,
}: UsePackArchitectureTableParams = {}) {
    const {
        tableRows,
        effectiveRowCount,
        tableTooltip,
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        tableKpiOptionItems,
        selectedKpis,
        toggleTableKpi,
        tableSearchInput,
        setTableSearchInput,
        submitTableSearch,
        tableSearchKeyword,
        isChartLoading,
        isTablePaginatedLoading,
        isChartPaginatedLoading,
        isChartTableRefetching,
        isChartTableLoaderRefetching,
        isChartPanelLoading,
        chartError,
        tableHasMorePages,
        fetchNextTablePage,
        activeSegmentKey,
        selectedColumnKey,
        selectSegment,
        selectTableCell,
        clearSelection,
    } = usePackArchitectureContext();

    const tableKpiOptions: KpiOption[] = useMemo(
        () =>
            tableKpiOptionItems
                .filter((item) => item.id)
                .map((item) => ({
                    id: item.id!,
                    label: item.label ?? item.id ?? '',
                })),
        [tableKpiOptionItems],
    );

    const { yAxisLabel, tableTitle } = useMemo(() => {
        const xLabel = getAxisLabel(xAxis, xAxisOptionGroups);
        const yLabel = getAxisLabel(yAxis, yAxisOptionGroups) || 'Y-Axis';
        const bubbleLabel = getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups);
        return {
            yAxisLabel: yLabel,
            tableTitle: buildPackArchitectureTableTitle(xLabel, bubbleLabel),
        };
    }, [xAxis, xAxisOptionGroups, yAxis, yAxisOptionGroups, bubbleRepresents, bubbleRepresentsOptionGroups]);

    const kpiOptions: KpiOption[] = useMemo(
        () => [
            {
                id: yAxis,
                label: formatTableKpiCheckboxLabel(yAxisLabel),
            },
            ...tableKpiOptions,
        ],
        [yAxis, yAxisLabel, tableKpiOptions],
    );

    const [search, setSearch] = [tableSearchInput, setTableSearchInput];
    const internalTableWrapperRef = useRef<HTMLDivElement | null>(null);
    const tableWrapperRef = tableWrapperRefProp ?? internalTableWrapperRef;

    const orderedSelectedKpis = useMemo(() => {
        const order = [yAxis, ...tableKpiOptions.map((option) => option.id)];
        return order.filter((id) => id && selectedKpis.includes(id));
    }, [selectedKpis, yAxis, tableKpiOptions]);

    const kpiIdMatcher = useMemo(
        () =>
            createPackArchitectureKpiIdMatcher({
                configuredKpiIds: collectConfiguredPackArchitectureKpiIds(
                    yAxisOptionGroups,
                    tableKpiOptionItems,
                    AXIS_PREVIEW_SALES_KPI_IDS,
                ),
                salesKpiIds: AXIS_PREVIEW_SALES_KPI_IDS,
            }),
        [tableKpiOptionItems, yAxisOptionGroups],
    );

    const { columns, tableRows: builtTableRows } = useMemo(() => buildTableData(tableRows, yAxis), [tableRows, yAxis]);
    const loadedColumnCount = columns.length;
    const visibleColumns = useMemo(() => columns.slice(0, MAX_VISIBLE_COLUMNS), [columns]);

    usePackArchitectureTableScroll({
        tableWrapperRef,
        builtTableRowCount: builtTableRows.length,
        visibleColumnCount: visibleColumns.length,
        loadedColumnCount,
        tableHasMorePages,
        isChartLoading,
        isTablePaginatedLoading,
        isChartPaginatedLoading,
        fetchNextTablePage,
        activeSegmentKey,
        selectedColumnKey,
        isSyncingTableFromChartRef,
        suppressChartToTableSyncRef,
    });

    const getRowClassName = useCallback(
        (record: TableRow) => {
            if (!activeSegmentKey) return 'table-row-clickable';
            if (record.key === activeSegmentKey) return 'table-row-selected table-row-clickable';
            return 'table-row-dimmed table-row-clickable';
        },
        [activeSegmentKey],
    );

    const handleRowClick = useCallback(
        (record: TableRow) => {
            selectSegment(record.key);
        },
        [selectSegment],
    );

    const clearHighlight = useCallback(() => {
        clearSelection();
    }, [clearSelection]);

    const handleSubmitSearch = useCallback(
        (override?: string) => {
            submitTableSearch(override);
        },
        [submitTableSearch],
    );

    const kpiTickCount = Math.max(orderedSelectedKpis.length, 1);

    const isAppendingPages = isTablePaginatedLoading && tableRows.length > 0;
    const showTableLoader = isChartTableLoaderRefetching || (isChartTableRefetching && loadedColumnCount === 0);

    const antColumns = useMemo(
        () =>
            buildPackArchitectureTableColumns({
                visibleColumns,
                orderedSelectedKpis,
                yAxis,
                activeSegmentKey,
                selectedColumnKey,
                onCellClick: selectTableCell,
                onColumnHeaderClick: clearHighlight,
                matchesKpiId: kpiIdMatcher.matches,
            }),
        [
            visibleColumns,
            orderedSelectedKpis,
            yAxis,
            activeSegmentKey,
            selectedColumnKey,
            selectTableCell,
            clearHighlight,
            kpiIdMatcher.matches,
        ],
    );

    const isFiltered = tableSearchKeyword.length > 0;
    const showTable =
        !isChartPanelLoading ||
        tableRows.length > 0 ||
        isChartTableRefetching ||
        tableSearchKeyword.length > 0;
    const isTableEmpty = loadedColumnCount === 0 && !showTableLoader && !isAppendingPages && !isChartTableRefetching;
    const emptyStateTitle = isFiltered ? 'No matching rows found' : 'No data found';
    const emptyStateDescription = getPackArchitectureTableEmptyDescription(chartError, isFiltered);
    const footerLabel = getPackArchitectureTableFooterLabel(
        showTableLoader,
        isAppendingPages,
        loadedColumnCount,
        effectiveRowCount,
    );

    return {
        tooltip: tableTooltip,
        tableTitle,
        kpiOptions,
        selectedKpis,
        search,
        setSearch,
        submitSearch: handleSubmitSearch,
        clearHighlight,
        toggleKpi: toggleTableKpi,
        tableWrapperRef,
        antColumns,
        filteredRows: builtTableRows,
        isFiltered,
        showTable,
        isTableEmpty,
        emptyStateTitle,
        emptyStateDescription,
        footerLabel,
        kpiTickCount,
        showTableLoader,
        isAppendingPages,
        tableLoaderMinHeight: TABLE_SCROLL_Y,
        tableEmptyStateMinHeight: TABLE_EMPTY_STATE_MIN_HEIGHT,
        getRowClassName,
        handleRowClick,
    };
}
